import { Check, ChevronDown, TriangleAlert } from 'lucide-react';
import type { KeyboardEvent, ReactNode } from 'react';
import { useState } from 'react';
import { Label } from '@/components/atoms/label';
import { cn } from '@/libs/utils/cn';
import { THIN_SCROLLBAR } from '@/libs/utils/thinScrollbar';
import { useSession } from '@/features/auth/context/SessionContext';
import { dealStageStyle } from '@/features/locations/constants/dealStages';
import { useMixpanel } from '@/hooks/useMixpanel';
import type { IHubSpotOwner, ILocation, INearbyPlace } from '@/types/api';
import {
  type GeoPoint,
  useAssignableHubSpotOwners,
  useHubSpotOwner,
  useLocationZoneCandidates,
  useNearbyPlaces,
} from '../services/dealCreation.api';
import {
  type DealCreationDraft,
  matchingPlaces,
  placeNameEdited,
  placePicked,
} from '../utils/dealCreationDraft';
import { LANDOWNER_TYPES, asLandownerType } from '../utils/landownerType';
import { DEALS_MIXPANEL_EVENTS, dealCreationPoiPickedPayload } from '../utils/mixpanelEvents';
import { DealField } from './DealField';

const IDENTIFIED_LEAD = dealStageStyle('Identified Lead');

interface DealStepProps {
  locationId: string;
  draft: DealCreationDraft;
  onChange: (patch: Partial<DealCreationDraft>) => void;
  /** The location's own point, shown read-only: the deal is created on it. */
  coordinates?: string;
  /**
   * The location's address lines, shown read-only. They are written on the deal as they are: the
   * deal sits at the location, so correcting the company's address does not move it.
   */
  addressLines?: Pick<ILocation, 'street' | 'postcode' | 'city' | 'country_code'>;
  /**
   * Where to look for the places offered as the POI. Absent when the location carries its own
   * place, which was attached by hand and names the deal as it is, or when it has no point.
   */
  nearbyPlacesAt?: GeoPoint;
}

/**
 * Step 3: the deal itself. The BD gives the POI and the city; the name is composed from the two
 * so every deal reads `<city> - <POI>` in the CRM. The pipeline, the stage, the coordinates and
 * the address are the location's or the system's and are shown read-only because the contract has
 * no field for them.
 */
export function DealStep({
  locationId,
  draft,
  onChange,
  coordinates,
  addressLines,
  nearbyPlacesAt,
}: DealStepProps) {
  const [isPickingPlace, setIsPickingPlace] = useState(false);
  // The row the arrow keys rest on. -1 is none, which is where typing puts it back.
  const [activePlace, setActivePlace] = useState(-1);
  const nearby = useNearbyPlaces(nearbyPlacesAt);
  const offered = nearby.data?.places ?? [];
  const places = matchingPlaces(offered, draft.poiName, draft.place);
  const isPlaceListOpen = isPickingPlace && nearbyPlacesAt !== undefined && nearby.isSuccess;
  const trackAction = useMixpanel((s) => s.trackAction);

  const pickPlace = (place: INearbyPlace) => {
    if (place !== draft.place) {
      trackAction(
        DEALS_MIXPANEL_EVENTS.CREATION_POI_PICKED,
        dealCreationPoiPickedPayload(place, offered, places.length !== offered.length),
      );
    }
    onChange(placePicked(place));
    setIsPickingPlace(false);
    setActivePlace(-1);
  };

  const movePlace = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!isPlaceListOpen) return;
    if (event.code === 'ArrowDown' || event.code === 'ArrowUp') {
      event.preventDefault();
      if (places.length === 0) return;
      const step = event.code === 'ArrowDown' ? 1 : -1;
      setActivePlace((activePlace + step + places.length) % places.length);
    } else if ((event.code === 'Enter' || event.code === 'NumpadEnter') && activePlace >= 0) {
      event.preventDefault();
      const place = places[activePlace];
      if (place) pickPlace(place);
    } else if (event.code === 'Escape') {
      event.preventDefault();
      setIsPickingPlace(false);
    }
  };
  const [ownerChecked, setOwnerChecked] = useState('');
  const [isPickingOwner, setIsPickingOwner] = useState(false);
  const { data: owner, isFetching: isCheckingOwner } = useHubSpotOwner(ownerChecked, true);
  const { data: assignable } = useAssignableHubSpotOwners(isPickingOwner);
  const { user } = useSession();
  const { data: zones } = useLocationZoneCandidates(locationId, true);
  const candidates = zones?.candidates ?? [];

  const ownerEmail = draft.ownerEmail.trim();
  const ownerAnswered = ownerChecked !== '' && ownerChecked === ownerEmail && !isCheckingOwner;
  const ownerName = owner ? [owner.first_name, owner.last_name].filter(Boolean).join(' ') : '';
  const suggestions = suggest(assignable?.owners ?? [], ownerEmail, user?.email);

  const pickOwner = (email: string) => {
    onChange({ ownerEmail: email });
    // Picked from the portal's own list, so it needs no Verify.
    setOwnerChecked(email);
    setIsPickingOwner(false);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <DealField
          id="deal-poi"
          label="POI name"
          required
          placeholder="POI name"
          value={draft.poiName}
          onFocus={() => setIsPickingPlace(true)}
          onBlur={() => setIsPickingPlace(false)}
          onKeyDown={movePlace}
          onChange={(poiName) => {
            onChange(placeNameEdited(draft, poiName));
            setActivePlace(-1);
          }}
        />
        {isPlaceListOpen && (
          <ul
            aria-label="Nearby places"
            className={cn(
              'absolute inset-x-0 top-full z-10 mt-1 max-h-56 overflow-y-auto rounded-lg border border-border bg-white py-1 shadow-modal',
              THIN_SCROLLBAR,
            )}
          >
            {places.length === 0 && (
              <li className="px-3 py-1.5 text-sm text-primary-500">
                {nearby.data?.places.length === 0 ? 'No POI within 250 m' : 'No matching POI'}
              </li>
            )}
            {places.map((place, index) => (
              <li key={`${place.name}:${place.latitude}:${place.longitude}`}>
                {/* mousedown: the input blurs first, so a click never lands. */}
                <button
                  type="button"
                  tabIndex={-1}
                  aria-selected={index === activePlace || undefined}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    pickPlace(place);
                  }}
                  className={cn(
                    'flex w-full items-baseline justify-between gap-3 px-3 py-1.5 text-left hover:bg-accent',
                    index === activePlace && 'bg-accent',
                  )}
                >
                  <span className="min-w-0 truncate text-sm text-primary-default">
                    {place.name}
                  </span>
                  <span className="shrink-0 text-[11px] text-primary-500">
                    {[place.type, `${Math.round(place.distance)} m`].filter(Boolean).join(' · ')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <DealField
        id="deal-city"
        label="City"
        value={draft.city}
        onChange={(city) => onChange({ city })}
      />

      <div className="flex flex-col gap-[3px]">
        <span className="text-[10.5px] uppercase tracking-[0.04em] text-primary-500">
          Deal name
        </span>
        <p className="truncate text-[13px] font-semibold text-primary-default">
          {draft.city.trim() || <span className="text-primary-400">City</span>}
          {' - '}
          {draft.poiName.trim() || <span className="text-primary-400">POI name</span>}
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="relative">
          <DealField
            id="deal-owner"
            label="Deal Owner Email"
            type="email"
            placeholder="bd@go-electra.com"
            value={draft.ownerEmail}
            onFocus={() => setIsPickingOwner(true)}
            onChange={(ownerEmail) => {
              onChange({ ownerEmail });
              setIsPickingOwner(true);
            }}
            action={{ label: 'Verify', onAct: () => setOwnerChecked(ownerEmail) }}
          />
          {isPickingOwner && suggestions.length > 0 && (
            <ul
              aria-label="Deal owner suggestions"
              className={cn(
                'absolute inset-x-0 top-full z-10 mt-1 max-h-56 overflow-y-auto rounded-lg border border-border bg-white py-1 shadow-modal',
                THIN_SCROLLBAR,
              )}
            >
              {suggestions.map((candidate) => (
                <li key={candidate.id}>
                  {/* mousedown: the input blurs first, so a click never lands. */}
                  <button
                    type="button"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      pickOwner(candidate.email);
                    }}
                    className="flex w-full flex-col items-start px-3 py-1.5 text-left hover:bg-accent"
                  >
                    <span className="text-sm text-primary-default">
                      {displayName(candidate) || candidate.email}
                    </span>
                    <span className="text-[11px] text-primary-500">{candidate.email}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {ownerAnswered && (
          <p
            className={cn(
              'flex items-start gap-1.5 text-[11.5px] leading-4',
              owner ? 'text-brand-800' : 'text-[#8a5a09]',
            )}
          >
            {owner ? (
              <>
                <Check className="mt-px size-3.5 shrink-0" aria-hidden />
                Assigned to {ownerName || owner.email}
              </>
            ) : (
              <>
                <TriangleAlert className="mt-px size-3.5 shrink-0" aria-hidden />
                No HubSpot user found for this email — the deal will be created with no owner.
              </>
            )}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-x-3.5">
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor="deal-site-type" className="text-xs font-normal text-primary-500">
            Site type
          </Label>
          <div className="relative">
            <ChevronDown
              className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-primary-500"
              aria-hidden
            />
            <select
              id="deal-site-type"
              value={draft.landownerType}
              onChange={(event) => onChange({ landownerType: asLandownerType(event.target.value) })}
              className="h-[42px] w-full appearance-none rounded-lg border border-border bg-white pr-9 pl-3 text-sm text-primary-default outline-none focus:border-brand-700"
            >
              <option value="">Select a site type</option>
              {LANDOWNER_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor="deal-zone" className="text-xs font-normal text-primary-500">
            Hunting zone
          </Label>
          {/* The UA draws its own arrow hard against the border, so it is suppressed and redrawn
            with the same inset as the field's text. pr-9 keeps a long zone name from running
            under it. */}
          <div className="relative">
            <ChevronDown
              className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-primary-500"
              aria-hidden
            />
            <select
              id="deal-zone"
              value={draft.zoneRef}
              onChange={(event) => onChange({ zoneRef: event.target.value })}
              className="h-[42px] w-full appearance-none rounded-lg border border-border bg-white pr-9 pl-3 text-sm text-primary-default outline-none focus:border-brand-700"
            >
              <option value="">
                {candidates.length === 0 ? 'No hunting zone nearby' : 'No hunting zone'}
              </option>
              {candidates.map((candidate) => (
                <option key={candidate.id} value={candidate.name}>
                  {candidate.name}
                  {!candidate.is_inside && ` · ${Math.round(candidate.distance)} m`}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <dl className="mt-1 flex w-full flex-col gap-4 pt-1.5 sm:flex-row sm:flex-wrap sm:justify-between sm:gap-y-4">
        <ReadOnlyField basis="sm:basis-[47%]" label="Pipeline">
          Real Estate
        </ReadOnlyField>
        <ReadOnlyField align="end" basis="sm:basis-[47%]" label="Stage">
          <span
            className="inline-flex items-center gap-1.5 rounded-full py-0.5 pr-2.5 pl-1.5 text-[11px] font-bold"
            style={{ backgroundColor: IDENTIFIED_LEAD.color, color: IDENTIFIED_LEAD.fg }}
          >
            <span className="size-1.5 rounded-full bg-current" aria-hidden />
            Identified Lead
          </span>
        </ReadOnlyField>
        {coordinates && (
          <ReadOnlyField basis="sm:basis-[47%]" label="Coordinates" tabular>
            {coordinates}
          </ReadOnlyField>
        )}
        {addressLines?.street && (
          <ReadOnlyField align="end" basis="sm:basis-[47%]" label="Street address">
            {addressLines.street}
          </ReadOnlyField>
        )}
        {/* The two short lines share one row: each is a word or a number, and giving each a half
            row left the panel half empty. The city is not among them — it has its own field
            above, and showing it twice read as two different values. */}
        <div className="flex w-full flex-col gap-4 sm:flex-row sm:justify-between sm:gap-x-6">
          {addressLines?.postcode && (
            <ReadOnlyField label="Postal code" tabular>
              {addressLines.postcode}
            </ReadOnlyField>
          )}
          {addressLines?.country_code && (
            <ReadOnlyField align="end" label="Country">
              {addressLines.country_code}
            </ReadOnlyField>
          )}
        </div>
      </dl>
    </div>
  );
}

function ReadOnlyField({
  label,
  tabular = false,
  basis,
  align = 'start',
  children,
}: {
  label: string;
  tabular?: boolean;
  /** Row share when the field sits in a distributed row; unset lets it size to its content. */
  basis?: string;
  /**
   * Which edge the pair sits against. The last field of a row reads as spaced from the first only
   * when its own text is pushed to the right edge too — left-aligning it leaves the gap inside the
   * field instead of between the two.
   */
  align?: 'start' | 'center' | 'end';
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-[3px]',
        basis,
        align === 'end' && 'items-start text-left sm:items-end sm:text-right',
        align === 'center' && 'items-center text-center',
      )}
    >
      <dt className="text-[10.5px] uppercase tracking-[0.04em] text-primary-500">{label}</dt>
      <dd
        className={cn(
          'truncate text-[13px] font-semibold text-primary-default',
          tabular && 'tabular-nums',
        )}
      >
        {children}
      </dd>
    </div>
  );
}

const MAX_OWNER_SUGGESTIONS = 6;

/** The owners offered for the typed fragment, the acting user first. */
function suggest(owners: IHubSpotOwner[], typed: string, me?: string): IHubSpotOwner[] {
  const mine = me?.toLowerCase();
  const ordered = [
    ...owners.filter((owner) => owner.email.toLowerCase() === mine),
    ...owners.filter((owner) => owner.email.toLowerCase() !== mine),
  ];

  // A whole address the portal holds is the field's default, not a fragment: narrowing to it
  // would answer the one case the list exists for with a list of one.
  const needle = typed.toLowerCase();
  const isWholeAddress = ordered.some((owner) => owner.email.toLowerCase() === needle);
  if (needle === '' || isWholeAddress) return ordered.slice(0, MAX_OWNER_SUGGESTIONS);

  return ordered
    .filter(
      (owner) =>
        owner.email.toLowerCase().includes(needle) ||
        displayName(owner).toLowerCase().includes(needle),
    )
    .slice(0, MAX_OWNER_SUGGESTIONS);
}

function displayName(owner: IHubSpotOwner): string {
  return [owner.first_name, owner.last_name].filter(Boolean).join(' ');
}
